import moment from 'moment-tz';

import {sweetAPI} from 'app/store/sweet-api';
import {
  createTagsListFromResponse,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  getProcedureDetailURLByHashid,
  PROCEDURES_API_URL,
  PROCEDURES_API_URL_COPY,
  PROCEDURES_VERSION_APPROVE_API_URL,
  PROCEDURES_VERSIONS_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {
  DOCUMENT_TAG,
  LANE_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
} from 'app/utils/rtk-tags';

import {
  standardizeRawProcedure,
  standardizeRawProcedureVersion,
  standardizeRawProcedureVersions,
} from './utils';

import type {
  ProceduresFetchParamsType,
  ProceduresLocationParamsType,
} from './types';
import type {CreateNewDocProps} from 'app/general/types';
import type {
  ListResponseType,
  ProcedureHashidSerializedType,
  ProcedureSerializedType,
  ProcedureVersionSerializedType,
  RawProcedureHashidSerializedType,
  RawProcedureSerializedType,
  RelatedTeamsSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      DOCUMENT_TAG,
      LANE_RTK_TAGS.TAGS,
      PROCEDURE_RTK_TAGS.TAGS,
      PROCEDURE_RTK_TAGS.HASHID,
      PROCEDURE_RTK_TAGS.VERSIONS,
    ],
  })

  .injectEndpoints({
    endpoints: (build) => ({
      procedureByHashid: build.query<ProcedureHashidSerializedType, string>({
        query: (hashid) => ({
          url: getProcedureDetailURLByHashid(hashid),
        }),
        transformResponse: (
          procedure: RawProcedureHashidSerializedType & {
            related: RelatedTeamsSerializedType;
          }
        ) => standardizeRawProcedure(procedure, procedure.related),
        providesTags: (response) => {
          return response != null
            ? [
                {type: PROCEDURE_RTK_TAGS.HASHID, id: response.url},
                {type: PROCEDURE_RTK_TAGS.HASHID, id: 'list'},
              ]
            : [];
        },
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the procedure.'),
        }),
      }),
      procedureById: build.query<ProcedureSerializedType, number>({
        query: (id) => ({
          url: `${PROCEDURES_API_URL}${id}/`,
        }),
        transformResponse: (
          procedure: RawProcedureSerializedType & {
            related: RelatedTeamsSerializedType;
          }
        ) => standardizeRawProcedure(procedure, procedure.related),
        providesTags: (response) =>
          response != null
            ? [{type: PROCEDURE_RTK_TAGS.TAGS, id: response.url}]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the procedure.'),
        }),
      }),
      procedures: build.query<
        ListResponseType<ProcedureSerializedType, RelatedTeamsSerializedType>,
        | {lane?: string}
        | {
            policy_id: number;
            fields: Array<string>;
          }
        | void
        | ProceduresFetchParamsType
      >({
        query: (params) => ({
          url: PROCEDURES_API_URL,
          params: params ?? {},
        }),
        providesTags: (response) =>
          createTagsListFromResponse(PROCEDURE_RTK_TAGS.TAGS, response, {
            addsListId: true,
            idGetter: (procedure) => procedure.url,
          }),
        transformResponse: (
          response: ListResponseType<
            RawProcedureSerializedType,
            RelatedTeamsSerializedType
          >
        ) => ({
          ...response,
          results: response.results.map((doc) =>
            standardizeRawProcedure(doc, response.related)
          ),
        }),
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the procedures.'),
        }),
      }),

      createProcedure: build.mutation<
        ProcedureSerializedType,
        CreateNewDocProps
      >({
        query: (data) => ({
          url: PROCEDURES_API_URL,
          method: 'POST',
          body: {
            steps_blocked_at: moment().utc().format(),
            ...data,
          },
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not create the procedure.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
        ],
      }),

      updateProcedure: build.mutation<
        ProcedureSerializedType,
        {url: string} & Partial<ProcedureSerializedType> & {
            successMessage?: string;
            errorMessage?: string;
          }
      >({
        query: ({url, ...body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        transformResponse: (
          procedure: RawProcedureSerializedType & {
            related: RelatedTeamsSerializedType;
          }
        ) => standardizeRawProcedure(procedure, procedure.related),
        onQueryStarted: showMessages({
          onSuccess: ({successMessage}) => successMessage ?? null,
          onError: (error, {errorMessage}) =>
            errorMessage ?? gt('Unable to update the procedure'),
        }),
        invalidatesTags: (result, error, {url}) => [
          {type: PROCEDURE_RTK_TAGS.TAGS, id: url},
          {type: PROCEDURE_RTK_TAGS.HASHID, id: url},
        ],
      }),

      procedureVersionById: build.query<
        ProcedureVersionSerializedType,
        {versionId: string | number}
      >({
        query: ({versionId}) => ({
          url: `${PROCEDURES_VERSIONS_API_URL}${versionId}/`,
        }),
        transformResponse: standardizeRawProcedureVersion,
        providesTags: (response) =>
          response != null
            ? [
                {type: PROCEDURE_RTK_TAGS.VERSIONS, id: response.url},
                {type: PROCEDURE_RTK_TAGS.VERSIONS, id: 'list'},
              ]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the version.'),
        }),
      }),

      procedureVersions: build.query<
        ListResponseType<ProcedureVersionSerializedType>,
        {
          objectId?: string | number;
          page?: string | number;
          page_size?: string;
        }
      >({
        query: ({objectId, page = 1, page_size = DEFAULT_PAGE_SIZE}) => ({
          url: PROCEDURES_VERSIONS_API_URL,
          params: {version_object_id: objectId, page, page_size},
        }),
        transformResponse: standardizeRawProcedureVersions,
        providesTags: providesTagsByURL(PROCEDURE_RTK_TAGS.VERSIONS),
        onQueryStarted: showMessages({
          onError: () => gt('Unable to load the procedure versions.'),
        }),
      }),
      updateProcedureVersion: build.mutation<
        ProcedureVersionSerializedType,
        {url: string} & Partial<ProcedureVersionSerializedType>
      >({
        query: ({url, ...version}) => ({
          url: url,
          method: 'PATCH',
          body: version,
        }),
        transformResponse: standardizeRawProcedureVersion,
        invalidatesTags: (result, error, {url}) => [
          {type: PROCEDURE_RTK_TAGS.VERSIONS, id: url},
        ],
      }),

      approveProcedureVersion: build.mutation<
        RawProcedureHashidSerializedType,
        {
          versionURL: string;
          options: {notify?: boolean; signoff?: boolean};
        }
      >({
        query: ({versionURL, options}) => ({
          method: 'POST',
          url: PROCEDURES_VERSION_APPROVE_API_URL,
          body: {url: versionURL, ...options},
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({options}) =>
              options.notify
                ? gt('Approved. Teammates will be notified')
                : gt('This version is now approved and live.'),
            onError: () => gt('Could not approve that version.'),
          })(args, api);
        },
        invalidatesTags: (response) => {
          return [
            {type: LANE_RTK_TAGS.TAGS, id: 'list'},
            {type: PROCEDURE_RTK_TAGS.VERSIONS, id: 'list'},
            {
              type: PROCEDURE_RTK_TAGS.VERSIONS,
              id: response?.current_version.url,
            },
            {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
            {type: PROCEDURE_RTK_TAGS.TAGS, id: response?.url},
            {type: PROCEDURE_RTK_TAGS.HASHID, id: response?.url},
          ];
        },
      }),
      duplicateProcedure: build.mutation<
        ProcedureSerializedType,
        {name: string; url: string; teams?: Array<string>}
      >({
        query: (body) => ({
          url: PROCEDURES_API_URL_COPY,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to duplicate'),
          })(args, api);
        },
        invalidatesTags: () => [{type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'}],
      }),
    }),
  });

export const {
  useProcedureByHashidQuery: useProcedureByHashid,
  useProcedureByIdQuery: useProcedureById,
  useProceduresQuery: useProcedures,
  useUpdateProcedureMutation: useUpdateProcedure,
  useCreateProcedureMutation: useCreateProcedure,
  useProcedureVersionByIdQuery: useProcedureVersionById,
  useProcedureVersionsQuery: useProcedureVersions,
  useUpdateProcedureVersionMutation: useUpdateProcedureVersion,
  useLazyProceduresQuery: useLazyProcedures,
  useApproveProcedureVersionMutation: useApproveProcedureVersion,
  useDuplicateProcedureMutation: useDuplicateProcedure,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: ProceduresLocationParamsType
): ProceduresFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];
  return {
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
    search: query.search,
    ordering: query.ordering,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    visible_to_user:
      query.visible_to_user != null ? query.visible_to_user : undefined,
    exclude_id: query.exclude_id,
    filter: query.filter,
    folder: query.folder,
    quiz_id: query.quiz_id,
  };
};
